<template>
  <div>
    <!-- Trigger Button (rendered inline) -->
    <div
      v-if="!modalOnly"
      @click.stop="openModal"
      class="drop-menu__item drop-menu__item--add-org"
      role="button"
    >
      <span class="ico-menu ico-menu--add">
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.5 0C3.36 0 0 3.36 0 7.5C0 11.64 3.36 15 7.5 15C11.64 15 15 11.64 15 7.5C15 3.36 11.64 0 7.5 0ZM11.25 8.25H8.25V11.25H6.75V8.25H3.75V6.75H6.75V3.75H8.25V6.75H11.25V8.25Z"
            fill="#107598"
          />
        </svg>
      </span>
      {{ $t("template.create_new_organization", "Create New Organization") }}
    </div>

    <!-- Modal Component -->
    <b-modal
      v-model="showModal"
      size="lg"
      content-class="modal-content-dashboard"
      @hidden="clearData"
      no-close-on-backdrop
    >
      <div slot="modal-header">
        <h2>{{ $t("template.create_new_organization_title", "Create New Organization") }}</h2>
        <div class="modal-header__hide" @click.prevent="hideModal">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
            <path
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
            ></path>
            <path d="M0 0h24v24H0z" fill="none"></path>
          </svg>
        </div>
      </div>

      <div slot="modal-footer">
        <button class="bttn bttn--lg bttn--blue" @click.prevent="hideModal" :disabled="sending">
          {{ $t("template.cancel", "Cancel") }}
        </button>
        <button @click.prevent="submitForm" class="bttn bttn--lg bttn--orange" :disabled="sending">
          <span v-if="sending">{{ $t("template.creating", "Creating...") }}</span>
          <span v-else>{{ $t("template.create_organization", "Create Organization") }}</span>
        </button>
      </div>

      <form id="create-organization" ref="createOrgForm" class="entities-data">
        <error v-if="errors" :errors="errors"></error>

        <div class="row">
          <div class="col-12">
            <div class="form-group" :class="{ 'has-error': vErrors.has('orgName') }">
              <label class="entities-data__label entities-data__label--required">
                {{ $t("template.organization_name", "Organization Name") }}
              </label>
              <b-form-input
                name="orgName"
                v-validate="'required|min:3'"
                v-model="organizationData.name"
                :placeholder="
                  $t('template.organization_name_placeholder', 'Enter organization name')
                "
              >
              </b-form-input>
              <span v-show="vErrors.has('orgName')" class="v-help is-danger">
                {{
                  $t(
                    "template.organization_name_required",
                    "Organization name is required (min 3 characters)"
                  )
                }}
              </span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-6">
            <div class="form-group">
              <label class="entities-data__label">
                {{ $t("template.contact_name", "Contact Name") }}
              </label>
              <b-form-input
                name="fullName"
                v-model="organizationData.full_name"
                :placeholder="$t('template.contact_name_placeholder', 'Enter contact person name')"
              >
              </b-form-input>
            </div>
          </div>

          <div class="col-12 col-md-6">
            <div class="form-group">
              <label class="entities-data__label">
                {{ $t("template.phone", "Phone") }}
              </label>
              <VueTelInput
                name="phone"
                v-model="organizationData.phone"
                v-bind="phoneInputAttrs"
                class="tw-h-[50px] tw-m-0 tw-flex tw-text-[#414359] tw-font-['VitoExtended-Light',sans-serif] tw-text-[15px] tw-shadow-none !tw-rounded tw-transition-all tw-duration-200 tw-border !tw-border-[#ced4da] focus-within:tw-border-2 focus-within:!tw-border-[#0084af] focus-within:!tw-shadow-none hover:!tw-border-[#0084af]"
                :inputOptions="{
                  placeholder: $t('template.phone_placeholder', 'Enter phone number'),
                  styleClasses: `tw-m-0 tw-px-[20px] tw-h-full tw-w-full tw-text-[#414359] tw-font-['VitoExtended-Light',sans-serif] tw-text-[15px]`,
                }"
                valid-characters-only
                @input="updatePhoneObject"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="form-group">
              <label class="entities-data__label">
                {{ $t("template.website", "Website") }}
              </label>
              <b-form-input
                name="website"
                v-model="organizationData.website"
                :placeholder="$t('template.website_placeholder', 'https://example.com')"
              >
              </b-form-input>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12 col-md-6">
            <div class="form-group">
              <label class="entities-data__label">
                {{ $t("template.timezone", "Timezone") }}
              </label>
              <b-form-select
                name="timezone"
                v-model="organizationData.timezone"
                :options="timezoneOptions"
              >
              </b-form-select>
            </div>
          </div>

          <div class="col-12 col-md-6">
            <div class="form-group">
              <label class="entities-data__label">
                {{ $t("template.language", "Language") }}
              </label>
              <b-form-select name="lang" v-model="organizationData.lang" :options="languageOptions">
              </b-form-select>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="form-group">
              <label class="entities-data__label">
                {{ $t("template.about_organization", "About") }}
              </label>
              <b-form-textarea
                name="about"
                v-model="organizationData.about"
                :placeholder="
                  $t(
                    'template.about_organization_placeholder',
                    'Brief description of your organization'
                  )
                "
                rows="3"
              >
              </b-form-textarea>
            </div>
          </div>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import error from "@/components/errors.vue";
import { notificationsMixin } from "@/mixins";
import { VueTelInput } from "vue-tel-input";
import { mapState, mapActions } from "vuex";

export default {
  name: "CreateOrganizationModal",
  mixins: [notificationsMixin],

  components: {
    error,
    VueTelInput,
  },

  props: {
    modalOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showModal: false,
      sending: false,
      errors: null,
      phoneObject: null,
      organizationData: {
        name: "",
        full_name: "",
        phone: "",
        website: "",
        about: "",
        timezone: "America/New_York",
        lang: "en",
      },
    };
  },

  computed: {
    ...mapState({
      timeZones: (state) => state.account.timeZones,
      languages: (state) => state.account.languages,
      defaultCountry: (state) => state.account.userCountry,
    }),

    timezoneOptions() {
      if (!this.timeZones || this.timeZones.length === 0) {
        return [{ value: "America/New_York", text: "America/New_York" }];
      }
      return this.timeZones.map((tz) => ({
        value: tz.attributes.timezone,
        text: tz.attributes.timezone,
      }));
    },

    languageOptions() {
      if (!this.languages || this.languages.length === 0) {
        return [{ value: "en", text: "English" }];
      }
      return this.languages.map((lang) => ({
        value: lang.attributes.code,
        text: lang.attributes.name,
      }));
    },

    autoDefaultCountry() {
      return this.defaultCountry === "N/A" || this.defaultCountry.length === 0;
    },

    phoneInputAttrs() {
      return {
        defaultCountry: this.defaultCountry,
        autoDefaultCountry: this.autoDefaultCountry,
        mode: "international",
      };
    },
  },

  methods: {
    ...mapActions(["createAdditionalOrganization", "getTimeZones", "getLanguages"]),

    openModal() {
      console.log("Opening Create Organization Modal");
      // Load timezones and languages if not already loaded
      if (!this.timeZones || this.timeZones.length === 0) {
        this.getTimeZones();
      }
      if (!this.languages || this.languages.length === 0) {
        this.getLanguages();
      }
      this.showModal = true;
    },

    hideModal() {
      this.showModal = false;
    },

    clearData() {
      this.errors = null;
      this.sending = false;
      this.showModal = false;
      this.phoneObject = null;
      this.organizationData = {
        name: "",
        full_name: "",
        phone: "",
        website: "",
        about: "",
        timezone: "America/New_York",
        lang: "en",
      };
      this.$validator.reset();
    },

    updatePhoneObject(phone, phoneObject) {
      this.phoneObject = phoneObject;
    },

    async submitForm() {
      const valid = await this.$validator.validateAll();
      if (!valid) {
        return;
      }

      this.sending = true;
      this.errors = null;

      try {
        // Use E.164 format phone number from phoneObject if available
        const dataToSubmit = {
          ...this.organizationData,
          phone: this.phoneObject?.number || this.organizationData.phone,
        };
        await this.createAdditionalOrganization(dataToSubmit);
        this.hideModal();
        this.$emit("organization-created");
      } catch (error) {
        console.error("Error creating organization:", error);
        if (error.response && error.response.data && error.response.data.errors) {
          this.errors = [this.$_notificationsMixin_string(error)];
        } else {
          this.errors = [
            this.$t(
              "template.create_organization_error",
              "Failed to create organization. Please try again."
            ),
          ];
        }
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.drop-menu__item--add-org {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  cursor: pointer;
  color: #107598;
  font-weight: 500;
  border-top: 1px solid #eee;

  &:hover {
    background-color: #f5f5f5;
  }
}

.ico-menu--add {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
